import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi, formsApi, contentApi } from '../lib/api-client';
import { portalExpansionApi } from '../lib/portal-expansion-api';
import { useState } from 'react';

export function DashboardPage() {
  const [period, setPeriod] = useState('6 months');
  const [calendarMonth, setCalendarMonth] = useState(9); // October (0-indexed)
  const [calendarYear, setCalendarYear] = useState(2026);
  const [calendarDay, setCalendarDay] = useState(27);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: adminApi.getDashboardStats,
  });

  const { data: onboardingSummary } = useQuery({
    queryKey: ['onboarding-summary'],
    queryFn: portalExpansionApi.getOnboardingSummary,
    retry: false,
  });

  const { data: pendingRegs, isLoading: regsLoading } = useQuery({
    queryKey: ['pending-registrations-dashboard'],
    queryFn: () => adminApi.getPendingRegistrations(1, 4),
  });

  const { data: formsData } = useQuery({
    queryKey: ['forms-submissions-count'],
    queryFn: async () => {
      const forms = await formsApi.getAllForms();
      const totalSubmissions =
        forms?.reduce((sum: number, form: any) => sum + (form._count?.submissions || 0), 0) || 0;
      return { totalSubmissions };
    },
  });

  const { data: contentData } = useQuery({
    queryKey: ['content-published-count'],
    queryFn: async () => {
      const content = await contentApi.getAllContent();
      const published = content.data?.filter((c: any) => c.status === 'published').length || 0;
      return { publishedCount: published };
    },
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-slate/20 border-t-slate rounded-full animate-spin" />
      </div>
    );
  }

  const total = stats?.totalMembers || 0;
  const active = stats?.activeMembers || 0;
  const pending = stats?.pendingMembers || 0;
  const suspended = stats?.suspendedMembers || 0;

  // Chart data based on period
  const chartData =
    period === '30 days'
      ? {
          values: [2, 3, 1, 4, 2, 5],
          labels: ['Oct 2', 'Oct 7', 'Oct 12', 'Oct 17', 'Oct 22', 'Oct 27'],
          max: 6,
        }
      : period === '12 months'
        ? {
            values: [4, 8, 11, 14, 10, 5],
            labels: ['Nov–Dec', 'Jan–Feb', 'Mar–Apr', 'May–Jun', 'Jul–Aug', 'Sep–Oct'],
            max: 20,
          }
        : {
            values: [4, 6, 9, 7, 10, 16],
            labels: ['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'],
            max: 20,
          };

  // Calendar logic
  const firstDay = new Date(calendarYear, calendarMonth, 1).getDay();
  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
  const monthName = new Date(calendarYear, calendarMonth, 1).toLocaleString('en-CA', {
    month: 'long',
  });

  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
    setCalendarDay(1);
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
    setCalendarDay(1);
  };

  return (
    <>
      {/* Page Header */}
      <div className="pagehead between">
        <div>
          <h1>Your community, at a glance.</h1>
          <p>Membership, requests, and the work ahead.</p>
        </div>
        <div className="date-control">
          <svg
            className="icon"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          27 October 2026
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-main">
          {/* Stats Grid */}
          <div className="stats">
            <StatCard label="Total members" value={total} sub="Registered accounts" icon="users" />
            <StatCard
              label="Active members"
              value={active}
              sub="Verified & connected"
              icon="shield"
              index={2}
            />
            <StatCard
              label="Pending review"
              value={pending}
              sub="Registration requests"
              icon="clock"
              index={3}
            />
            <StatCard
              label="Suspended"
              value={suspended}
              sub="Restricted accounts"
              icon="user"
              index={4}
            />
          </div>

          {/* Activity Chart */}
          <section className="card activity-card">
            <div className="between cardhead">
              <div>
                <h2>Registration activity</h2>
                <p className="small">A view of new connections</p>
              </div>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="text-[10px] min-h-[30px] px-[10px] py-[5px] rounded-[7px] border-[#eceee6] bg-[#fafbf7] text-[#68716d]"
              >
                <option>30 days</option>
                <option>6 months</option>
                <option>12 months</option>
              </select>
            </div>

            <BarChart data={chartData} />
          </section>

          {/* Registration Requests */}
          <section className="card requests-card">
            <div className="between cardhead">
              <div>
                <h2>
                  Registration requests <span className="number-tag">{pending}</span>
                </h2>
                <p className="small">Members waiting to join the portal</p>
              </div>
              <Link to="/registrations" className="quiet-link">
                View all
                <svg
                  className="icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            {regsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-slate/20 border-t-slate rounded-full animate-spin" />
              </div>
            ) : pending > 0 && pendingRegs?.data && pendingRegs.data.length > 0 ? (
              <>
                {pendingRegs.data.map((reg: any) => (
                  <RequestRow
                    key={reg.id}
                    name={`${reg.firstName} ${reg.lastName}`}
                    email={reg.email}
                    date={new Date(reg.createdAt).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  />
                ))}
              </>
            ) : (
              <div className="empty">
                <svg
                  className="icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <h3>All caught up.</h3>
                <p>There are no registrations waiting for review.</p>
              </div>
            )}
          </section>

          {/* Bottom Section */}
          <div className="overview-bottom">
            <section className="card">
              <div className="between cardhead">
                <h2>Service applications</h2>
                <Link to="/forms" className="quiet-link">
                  View all
                  <svg
                    className="icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.65}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              {formsData && formsData.totalSubmissions > 0 ? (
                <div className="content-summary">
                  <strong>{formsData.totalSubmissions}</strong>
                  <div>
                    <p>Form {formsData.totalSubmissions === 1 ? 'submission' : 'submissions'}</p>
                    <small>Awaiting review</small>
                  </div>
                </div>
              ) : (
                <div className="mini-empty">
                  <svg
                    className="icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.65}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                  <div>
                    <h3>No applications to review</h3>
                    <p>New submissions will appear here.</p>
                  </div>
                </div>
              )}
            </section>

            <section className="card">
              <div className="between cardhead">
                <h2>Published content</h2>
                <Link to="/content" className="quiet-link">
                  Manage
                  <svg
                    className="icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.65}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              {contentData && contentData.publishedCount > 0 ? (
                <div className="content-summary">
                  <strong>{contentData.publishedCount}</strong>
                  <div>
                    <p>
                      {contentData.publishedCount === 1 ? 'Community update' : 'Community updates'}
                    </p>
                    <small>Available in the member portal</small>
                  </div>
                </div>
              ) : (
                <div className="mini-empty">
                  <svg
                    className="icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.65}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div>
                    <h3>No published content</h3>
                    <p>Publish updates to share with members.</p>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>

        {/* Right Rail */}
        <aside className="dashboard-rail">
          {/* Attention Card */}
          <section className="attention-card">
            <div className="between">
              <span className="rail-kicker">Needs your attention</span>
              <svg
                className="icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </div>
            <strong className="attention-number">{String(pending).padStart(2, '0')}</strong>
            <h2>New registrations</h2>
            <p>Review their details and help them get started.</p>
            <Link to="/registrations" className="btn rail-button">
              Review requests
              <svg
                className="icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <div className="attention-decoration">
              <i></i>
              <i></i>
              <i></i>
            </div>
          </section>

          {/* Calendar */}
          <Calendar
            month={calendarMonth}
            year={calendarYear}
            day={calendarDay}
            monthName={monthName}
            firstDay={firstDay}
            daysInMonth={daysInMonth}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onDayClick={setCalendarDay}
          />

          {/* Meetings */}
          <section className="card rail-meetings">
            <div className="between cardhead">
              <h2>Meetings</h2>
              <Link to="/meetings" className="iconbtn miniicon ghost">
                <svg
                  className="icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            {onboardingSummary?.nextMeeting ? (
              <div className="meeting-card">
                <h3>{onboardingSummary.nextMeeting.title}</h3>
                <p className="meeting-date">
                  {new Date(onboardingSummary.nextMeeting.scheduledAt).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
                {onboardingSummary.nextMeeting.registeredCount !== null && (
                  <p className="meeting-count">
                    {onboardingSummary.nextMeeting.registeredCount} registered
                  </p>
                )}
              </div>
            ) : (
              <div className="rail-empty">
                <svg
                  className="icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p>No meetings scheduled</p>
                <Link to="/meetings" className="link">
                  Schedule a meeting
                </Link>
              </div>
            )}
          </section>

          {/* Onboarding & Funding Stats */}
          {onboardingSummary && (
            <>
              <section className="card rail-stats">
                <h2 className="rail-heading">Onboarding</h2>
                <div className="stat-row">
                  <span className="stat-label">Invites sent</span>
                  <strong>{onboardingSummary.invitesSent}</strong>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Members verified</span>
                  <strong>{onboardingSummary.membersVerified}</strong>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Pending verification</span>
                  <strong>{onboardingSummary.verificationsPending}</strong>
                </div>
                <Link to="/onboarding-review" className="rail-link">
                  Review onboarding →
                </Link>
              </section>

              {onboardingSummary.fundingCounts && Object.keys(onboardingSummary.fundingCounts).length > 0 && (
                <section className="card rail-stats">
                  <h2 className="rail-heading">Funding Applications</h2>
                  {Object.entries(onboardingSummary.fundingCounts).map(([status, count]) => (
                    <div key={status} className="stat-row">
                      <span className="stat-label">{status.replace(/_/g, ' ')}</span>
                      <strong>{count as number}</strong>
                    </div>
                  ))}
                  <Link to="/funding-applications" className="rail-link">
                    Review applications →
                  </Link>
                </section>
              )}
            </>
          )}
        </aside>
      </div>

      <style>{`
        .pagehead {
          margin-bottom: 25px;
          gap: 18px;
          align-items: center;
          display: flex;
          justify-content: space-between;
        }

        .pagehead h1 {
          font-size: 30px;
          font-weight: 600;
          letter-spacing: -0.85px;
          color: #313739;
        }

        .pagehead p {
          font-size: 13px;
          margin-top: 6px;
          color: #68716d;
        }

        .date-control {
          display: flex;
          align-items: center;
          gap: 9px;
          font-size: 11px;
          background: white;
          border-radius: 8px;
          padding: 10px 13px;
          white-space: nowrap;
          color: #424a47;
        }

        .date-control .icon {
          height: 14px;
          width: 14px;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 286px;
          gap: 22px;
          align-items: start;
        }

        .dashboard-main,
        .dashboard-rail {
          display: flex;
          flex-direction: column;
          gap: 22px;
          min-width: 0;
        }

        .dashboard-rail {
          gap: 20px;
        }

        .card {
          padding: 22px;
          background: #fff;
          border: 0;
          border-radius: 15px;
          box-shadow: none;
          min-width: 0;
        }

        .cardhead {
          margin-bottom: 19px;
        }

        .cardhead p {
          margin-top: 3px;
        }

        .cardhead h2 {
          font-size: 18px;
          letter-spacing: -0.35px;
          font-weight: 600;
          color: #313739;
        }

        .cardhead .small {
          font-size: 12px;
          color: #68716d;
        }

        .stats {
          gap: 14px;
          margin: 0;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          display: grid;
        }

        .between {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .quiet-link {
          font-size: 11px;
          color: #68716d;
          display: flex;
          align-items: center;
          gap: 7px;
          white-space: nowrap;
        }

        .quiet-link:hover {
          color: #242728;
        }

        .quiet-link .icon {
          width: 13px;
          height: 13px;
        }

        .number-tag {
          font-size: 10px;
          font-weight: 500;
          background: #f7eee6;
          color: #a86b3a;
          border-radius: 5px;
          vertical-align: middle;
          padding: 3px 6px;
          margin-left: 5px;
        }

        .requests-card {
          padding-bottom: 7px;
        }

        .empty {
          padding: 28px 12px;
          text-align: center;
        }

        .empty .icon {
          width: 32px;
          height: 32px;
          color: #68716d;
          margin-bottom: 12px;
        }

        .empty h3 {
          font-size: 15px;
          font-weight: 600;
          color: #68716d;
        }

        .empty p {
          font-size: 12px;
          color: #68716d;
          margin-top: 4px;
        }

        .overview-bottom {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 20px;
        }

        .mini-empty {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px 0;
        }

        .mini-empty > .icon {
          width: 24px;
          height: 24px;
          color: #68716d;
        }

        .mini-empty h3 {
          font-size: 12px;
          font-weight: 500;
          color: #68716d;
        }

        .mini-empty p {
          font-size: 10px;
          margin-top: 4px;
          color: #68716d;
        }

        .content-summary {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 7px 0;
        }

        .content-summary > strong {
          font-size: 35px;
          font-weight: 500;
          line-height: 1;
          color: #4b607f;
        }

        .content-summary p {
          font-size: 11px;
          color: #68716d;
        }

        .content-summary small {
          font-size: 9px;
          display: block;
          margin-top: 2px;
        }

        .attention-card {
          background: #4b607f;
          color: #fff;
          padding: 23px;
          border-radius: 15px;
          position: relative;
          overflow: hidden;
        }

        .rail-kicker {
          text-transform: none;
          letter-spacing: 0;
          font-size: 11px;
          color: #c5d0dd;
        }

        .attention-card .between > .icon {
          width: 17px;
          height: 17px;
          color: #afc1d5;
        }

        .attention-number {
          display: block;
          font-size: 56px;
          line-height: 1;
          margin: 21px 0 13px;
          font-weight: 500;
          color: #edbd98;
          letter-spacing: -2px;
        }

        .attention-card h2 {
          color: #fff;
          font-size: 19px;
          font-weight: 500;
          margin-bottom: 7px;
        }

        .attention-card p {
          font-size: 12px;
          line-height: 1.6;
          color: #bdcadd;
          max-width: 180px;
        }

        .rail-button {
          margin-top: 22px;
          background: #e8d8c9 !important;
          color: #3f4a58 !important;
          border: 0 !important;
          min-height: 37px !important;
          font-size: 11px !important;
          gap: 18px !important;
          padding: 9px 13px !important;
          border-radius: 8px !important;
          width: 100%;
          justify-content: space-between !important;
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
        }

        .rail-button:hover {
          background: #f2e7dd !important;
        }

        .rail-button .icon {
          height: 14px;
          width: 14px;
        }

        .attention-decoration {
          position: absolute;
          width: 150px;
          height: 150px;
          right: -100px;
          top: 60px;
          border: 1px solid #62748b;
          border-radius: 50%;
          pointer-events: none;
        }

        .attention-decoration i {
          position: absolute;
          inset: -20px;
          border: 1px solid #62748b;
          border-radius: 50%;
        }

        .attention-decoration i:nth-child(2) {
          inset: -40px;
        }

        .attention-decoration i:nth-child(3) {
          inset: -60px;
        }

        .rail-meetings {
          padding: 19px 21px;
        }

        .rail-meetings h2 {
          font-size: 15px;
        }

        .rail-meetings .cardhead {
          margin-bottom: 9px;
        }

        .rail-empty {
          padding: 14px 0 8px;
          text-align: center;
        }

        .rail-empty > .icon {
          height: 23px;
          width: 23px;
          color: #68716d;
          margin-bottom: 7px;
        }

        .rail-empty p {
          font-size: 12px;
          color: #68716d;
        }

        .rail-empty .link {
          font-size: 10px;
          margin-top: 7px;
          color: #424a47;
          font-weight: 500;
          background: none;
          border: 0;
          padding: 0;
        }

        .iconbtn.miniicon {
          width: 29px;
          height: 29px;
          min-height: 29px;
          padding: 6px;
        }

        .icon {
          width: 18px;
          height: 18px;
          fill: none;
          stroke: currentColor;
          stroke-width: 1.65;
          stroke-linecap: round;
          stroke-linejoin: round;
          flex-shrink: 0;
        }

        .meeting-card {
          padding: 9px 0;
        }

        .meeting-card h3 {
          font-size: 12px;
          font-weight: 500;
          color: #424a47;
          margin-bottom: 5px;
        }

        .meeting-date {
          font-size: 11px;
          color: #68716d;
          margin-bottom: 4px;
        }

        .meeting-count {
          font-size: 10px;
          color: #4b607f;
          font-weight: 500;
        }

        .rail-stats {
          padding: 19px 21px;
        }

        .rail-heading {
          font-size: 15px;
          font-weight: 600;
          color: #313739;
          margin-bottom: 12px;
        }

        .stat-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f2f3ee;
        }

        .stat-row:last-of-type {
          border-bottom: 0;
        }

        .stat-label {
          font-size: 11px;
          color: #68716d;
          text-transform: capitalize;
        }

        .stat-row strong {
          font-size: 14px;
          font-weight: 600;
          color: #4b607f;
        }

        .rail-link {
          display: block;
          margin-top: 12px;
          font-size: 11px;
          color: #4b607f;
          font-weight: 500;
          text-align: center;
        }

        .rail-link:hover {
          color: #293849;
        }

        @media (max-width: 1220px) {
          .dashboard-grid {
            grid-template-columns: minmax(0, 1fr) 250px;
            gap: 18px;
          }

          .dashboard-main,
          .dashboard-rail {
            gap: 18px;
          }

          .stats {
            gap: 10px;
          }
        }

        @media (max-width: 1020px) {
          .dashboard-grid {
            grid-template-columns: minmax(0, 1fr);
          }

          .dashboard-rail {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .dashboard-rail .rail-meetings {
            grid-column: 1 / -1;
          }

          .pagehead .date-control {
            display: none;
          }
        }

        @media (max-width: 760px) {
          .dashboard-grid {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }

          .dashboard-main,
          .dashboard-rail {
            width: 100%;
            gap: 18px;
          }

          .dashboard-rail {
            grid-template-columns: 1fr;
            display: grid;
          }

          .dashboard-rail .rail-meetings {
            grid-column: auto;
          }

          .stats {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }

          .pagehead {
            margin-bottom: 23px;
            gap: 16px;
            align-items: flex-start;
            flex-direction: column;
          }

          .pagehead h1 {
            font-size: 27px;
            line-height: 1.2;
          }

          .overview-bottom {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
}

function StatCard({
  label,
  value,
  sub,
  icon,
  index: _index = 1,
}: {
  label: string;
  value: number;
  sub: string;
  icon: string;
  index?: number;
}) {
  const icons = {
    users:
      'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    shield:
      'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    clock: 'M12 8v5l3 2 M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0',
    user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  };

  return (
    <div className="card stat">
      <div className="between">
        <span className="statlabel">{label}</span>
        <span className="iconbox">
          <svg
            className="icon"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={icons[icon as keyof typeof icons]}
            />
          </svg>
        </span>
      </div>
      <strong>{value}</strong>
      <div className="stat-bottom">
        <span className="stat-marker"></span>
        <small>{sub}</small>
      </div>

      <style>{`
        .stat {
          padding: 17px 17px 15px;
          border-radius: 12px;
        }

        .statlabel {
          font-size: 11px !important;
          color: #68716d !important;
          white-space: nowrap;
        }

        .stat .iconbox {
          position: static;
          width: 25px;
          height: 25px;
          background: #f4f3ee;
          border-radius: 7px;
          color: #68716d;
          display: grid;
          place-items: center;
        }

        .stat .iconbox .icon {
          width: 14px;
          height: 14px;
        }

        .stat strong {
          font-size: 30px;
          line-height: 1.1;
          margin: 12px 0 15px;
          letter-spacing: -1px;
          font-weight: 600;
          display: block;
        }

        .stat-bottom {
          display: flex;
          gap: 6px;
          align-items: center;
        }

        .stat-bottom small {
          font-size: 9px;
          color: #68716d;
          line-height: 1.3;
        }

        .stat-marker {
          height: 4px;
          width: 4px;
          border-radius: 50%;
          background: #4b607f;
          flex-shrink: 0;
        }

        .stat:nth-child(2) .iconbox {
          background: #eaf0f5;
          color: #4b607f;
        }

        .stat:nth-child(3) .stat-marker {
          background: #f3701e;
        }

        .stat:nth-child(3) .iconbox {
          background: #fff0e5;
          color: #b6642a;
        }

        .stat:nth-child(4) .stat-marker {
          background: #c7b6a6;
        }

        @media (max-width: 760px) {
          .stat {
            padding: 18px;
          }

          .stat .iconbox {
            display: grid;
          }

          .statlabel {
            font-size: 12px !important;
          }

          .stat strong {
            font-size: 31px;
            margin: 12px 0 15px;
          }

          .stat-bottom small {
            font-size: 10px;
          }
        }
      `}</style>
    </div>
  );
}

function BarChart({ data }: { data: { values: number[]; labels: string[]; max: number } }) {
  const { values, labels, max } = data;
  const gridLines = [max, max * 0.75, max * 0.5, max * 0.25, 0];

  return (
    <>
      <div className="bar-chart">
        <div className="bar-yaxis">
          {gridLines.map((v, i) => (
            <span key={i}>{Number.isInteger(v) ? v : v.toFixed(1)}</span>
          ))}
        </div>
        <div className="bar-plot">
          <div className="bar-grid">
            {[0, 1, 2, 3, 4].map((i) => (
              <i key={i}></i>
            ))}
          </div>
          <div className="bar-columns">
            {values.map((v, i) => (
              <button key={i} type="button" className={`bar-column ${i === 5 ? 'latest' : ''}`}>
                <span className="bar-number">{v}</span>
                <span className="bar" style={{ '--height': v / max } as any}></span>
                <span className="bar-label">{labels[i]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className="charttooltip">Illustrative registration activity</p>

      <style>{`
        .bar-chart {
          height: 210px;
          display: flex;
          gap: 12px;
          padding-top: 8px;
        }

        .bar-yaxis {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          font-size: 10px;
          color: #68716d;
          padding-bottom: 30px;
          min-width: 18px;
          text-align: right;
        }

        .bar-plot {
          flex: 1;
          min-width: 0;
          position: relative;
        }

        .bar-grid {
          position: absolute;
          inset: 0 0 30px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .bar-grid i {
          border-top: 1px solid #f1f2ee;
          width: 100%;
        }

        .bar-columns {
          display: flex;
          height: 100%;
          align-items: flex-end;
          justify-content: space-around;
          gap: 8px;
          position: relative;
        }

        .bar-column {
          background: none;
          border: 0;
          color: #68716d;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          padding: 0 5px;
          min-width: 35px;
          height: 100%;
          position: relative;
          font-size: 11px;
          flex: 1;
        }

        .bar {
          display: block;
          width: 20px;
          max-width: 75%;
          height: calc((100% - 48px) * var(--height));
          border-radius: 4px 4px 2px 2px;
          background: #4b607f;
          transition: background 0.2s, height 0.4s;
          animation: barGrow 0.7s cubic-bezier(0.2, 0.6, 0.3, 1) both;
          transform-origin: bottom;
        }

        .bar-column.latest .bar {
          background: #f3701e;
        }

        .bar-column:hover .bar,
        .bar-column:focus-visible .bar {
          background: #293849;
        }

        .bar-column.latest:hover .bar {
          background: #d45d14;
        }

        .bar-number {
          font-size: 10px;
          color: #68716d;
          margin-bottom: 5px;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .bar-column:hover .bar-number,
        .bar-column:focus-visible .bar-number,
        .bar-column.latest .bar-number {
          opacity: 1;
        }

        .bar-label {
          margin-top: 11px;
          height: 19px;
          font-size: 10px;
          white-space: nowrap;
        }

        .charttooltip {
          font-size: 10px;
          color: #68716d;
          margin: 9px 0 0 31px;
          min-height: 15px;
        }

        @keyframes barGrow {
          from {
            scale: 1 0;
          }
          to {
            scale: 1 1;
          }
        }

        @media (max-width: 760px) {
          .bar-chart {
            height: 205px;
            gap: 9px;
          }

          .bar-columns {
            gap: 3px;
          }

          .bar-column {
            min-width: 25px;
            padding: 0 2px;
          }

          .bar {
            width: 17px;
          }

          .bar-label {
            font-size: 9px;
          }

          .bar-yaxis {
            font-size: 9px;
            min-width: 14px;
          }
        }
      `}</style>
    </>
  );
}

function RequestRow({ name, email, date }: { name: string; email: string; date: string }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('');

  return (
    <div className="request-row">
      <div className="person">
        <span className={`avatar ${name.length % 2 ? 'clay' : ''}`}>{initials}</span>
        <div>
          <strong>{name}</strong>
          <small>{email}</small>
        </div>
      </div>
      <span className="request-date">{date}</span>
      <button className="review-btn">
        Review
        <svg
          className="icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.65}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <style>{`
        .request-row {
          display: flex;
          gap: 12px;
          align-items: center;
          border-top: 1px solid #f2f3ee;
          padding: 15px 0;
        }

        .request-row .person {
          flex: 1;
          min-width: 0;
        }

        .person {
          gap: 11px;
          display: flex;
          align-items: center;
        }

        .person > div {
          min-width: 0;
        }

        .person strong {
          font-size: 12px;
          font-weight: 500;
          color: #424a47;
          display: block;
        }

        .person small {
          font-size: 10px;
          color: #68716d;
          margin-top: 1px;
          display: block;
        }

        .avatar {
          width: 32px;
          height: 32px;
          font-size: 10px;
          background: #e8edf2;
          color: #4b607f;
          font-weight: 500;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .avatar.clay {
          background: #f0e4d9;
          color: #a16a3b;
        }

        .request-date {
          font-size: 10px;
          color: #68716d;
          white-space: nowrap;
        }

        .review-btn {
          font-size: 10px !important;
          min-height: 30px !important;
          padding: 5px 9px !important;
          border-radius: 7px !important;
          gap: 10px !important;
          background: #f6f6f1 !important;
          color: #424a47 !important;
          border: 0 !important;
          display: inline-flex;
          align-items: center;
        }

        .review-btn .icon {
          width: 12px !important;
          height: 12px !important;
        }

        .review-btn:hover {
          background: #eeede5 !important;
        }

        @media (max-width: 760px) {
          .request-row {
            gap: 9px;
          }

          .request-row .person {
            gap: 9px;
          }

          .request-row .person strong {
            font-size: 11px;
          }

          .request-row .person small {
            font-size: 9px;
            max-width: 155px;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .request-row .person .avatar {
            width: 29px;
            height: 29px;
            font-size: 9px;
          }

          .request-date {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

function Calendar({
  month: _month,
  year,
  day,
  monthName,
  firstDay,
  daysInMonth,
  onPrevMonth,
  onNextMonth,
  onDayClick,
}: {
  month: number;
  year: number;
  day: number;
  monthName: string;
  firstDay: number;
  daysInMonth: number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onDayClick: (day: number) => void;
}) {
  return (
    <section className="card calendar-card">
      <div className="calendar-head">
        <button className="iconbtn miniicon ghost" onClick={onPrevMonth}>
          <svg
            className="icon"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3>
          {monthName} <span>{year}</span>
        </h3>
        <button className="iconbtn miniicon ghost" onClick={onNextMonth}>
          <svg
            className="icon"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="calendar-week">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>

      <div className="calendar-days">
        {Array.from({ length: firstDay }, (_, i) => (
          <span key={`empty-${i}`}></span>
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => (
          <button
            key={i + 1}
            className={i + 1 === day ? 'selected' : ''}
            onClick={() => onDayClick(i + 1)}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <div className="calendar-selection">
        <span>
          {day} {monthName}
        </span>
        <small>No meetings scheduled</small>
      </div>

      <style>{`
        .calendar-card {
          padding: 17px 20px;
        }

        .calendar-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          margin-bottom: 17px;
        }

        .calendar-head h3 {
          font-size: 12px;
          font-weight: 500;
          color: #424a47;
        }

        .calendar-head h3 span {
          color: #68716d;
          margin-left: 3px;
        }

        .calendar-head .icon {
          width: 12px;
          height: 12px;
        }

        .iconbtn.miniicon.ghost {
          width: 25px;
          height: 25px;
          min-height: 25px;
          padding: 5px;
          border-radius: 6px;
          background: #f8f9f3;
          color: #68716d;
          border: 0;
        }

        .calendar-week {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          font-size: 9px;
          text-align: center;
          color: #68716d;
          background: #fafbf7;
          padding: 5px 0;
          border-radius: 5px;
          margin-bottom: 7px;
        }

        .calendar-days {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          row-gap: 3px;
          text-align: center;
        }

        .calendar-days button {
          border: 0;
          background: none;
          min-width: 0;
          height: 27px;
          font-size: 10px;
          color: #68716d;
          border-radius: 50%;
          position: relative;
          margin: 0 2px;
          padding: 0;
          transition: background 0.15s;
        }

        .calendar-days button:hover {
          background: #f0eee5;
        }

        .calendar-days button.selected {
          background: #e8d8c9;
          color: #424a47;
          font-weight: 600;
        }

        .calendar-selection {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          margin-top: 15px;
          border-top: 1px solid #f0f2e9;
          padding-top: 13px;
          font-size: 9px;
          color: #68716d;
        }

        .calendar-selection small {
          font-size: 9px;
          color: #68716d;
        }

        @media (max-width: 760px) {
          .calendar-card {
            padding: 21px;
          }

          .calendar-head h3 {
            font-size: 14px;
          }

          .calendar-head {
            margin-bottom: 18px;
          }

          .calendar-head .iconbtn {
            width: 30px;
            height: 30px;
            min-height: 30px;
          }

          .calendar-week {
            font-size: 11px;
            padding: 7px;
          }

          .calendar-days {
            row-gap: 5px;
          }

          .calendar-days button {
            height: 36px;
            font-size: 12px;
            margin: 0 3px;
          }

          .calendar-selection {
            font-size: 11px;
            margin-top: 18px;
            padding-top: 17px;
          }

          .calendar-selection small {
            font-size: 10px;
          }
        }
      `}</style>
    </section>
  );
}
